'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import type { FetchBlogsQuery } from '@eliadmin/core/src/api/blogs/blogs-api';
import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import type { BlogCategory } from '@eliadmin/core/src/api/blogs/blogs-types';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Card, CardContent } from '@eliadmin/core/src/components/ui/card';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { createContextStore } from '@eliadmin/core/src/stores/context-store';
import { generateBlogKey } from '@eliadmin/core/src/utils/blog-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { useSearchParams } from 'next/navigation';
import { useMemo } from 'react';

const [BlogCategoriesProvider, useBlogCategories] = createContextStore<
  { categories: BlogCategory[]; activeCategoryIds: string[] },
  { setActiveCategories: (categoryIds: string[]) => void }
>(
  {
    categories: [],
    activeCategoryIds: [],
  },
  (set, get) => ({
    setActiveCategories: (categoryIds: string[]) => set({ activeCategoryIds: categoryIds }),
  }),
);

export { BlogCategoriesProvider, useBlogCategories };

export type BlogCardsProps = {
  query: FetchBlogsQuery;
  className?: {
    blog?: {
      main?: string;
      link?: string;
      image?: string;
      title?: string;
      date?: string;
      contentContainer?: string;
      content?: string;
    };
  };
};

export const BlogCards = ({ query: queryProp }: BlogCardsProps) => {
  const locale = 'sk';
  const { t } = useTranslations();

  const searchParams = useSearchParams();

  const search = searchParams.get('search');
  const activeCategories = useBlogCategories((s) => s.activeCategoryIds);

  const query: FetchBlogsQuery = { results: 10, order: 'desc', orderBy: 'id' };

  const fetchBlogsFn = async ({ pageParam = 1 }) => {
    const { data } = await fetchBlogs({
      locale,
      query: { ...query, page: pageParam } as any,
    });
    return data;
  };

  const {
    data: blogs,
    fetchNextPage,
    isFetching,
  } = useSuspenseInfiniteQuery({
    initialPageParam: 0,
    queryKey: ['blogs', query],
    queryFn: fetchBlogsFn,
    getNextPageParam: (lastPage, pages) => pages.length + 1,
  });

  return (
    <>
      <div
        className={cn(
          'grid w-full grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
          isFetching && 'opacity-30',
        )}
      >
        {blogs?.pages.map((p) => {
          return p.map((blog, i) => {
            if (i === 0 && query.search) return;
            return (
              <Card
                key={generateBlogKey(blog)}
                className={cn(
                  'relative col-span-1 w-full rounded-none border-none bg-transparent',
                  'ea-blog-card',
                )}
              >
                <Link
                  href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
                  className={cn('ea-blog-card__link h-full')}
                  prefetch={false}
                >
                  <CardContent
                    className={cn('flex h-full flex-col rounded-none', 'ea-blog-card__content')}
                  >
                    <Image
                      alt={resolveImageAlt(blog?.image, blog?.name, 'image')}
                      src={blog.image?.url || '/images/placeholder.png'}
                      width="0"
                      height="0"
                      sizes="100vw"
                      className={cn(
                        'h-[210px] w-full rounded-none object-cover object-center',
                        'ea-blog-card__image',
                      )}
                    />
                    <div className={cn('flex flex-col', 'ea-blog-card__content-container')}>
                      <div className={cn('my-2 text-sm text-[#565656]', 'ea-blogcard_date')}>
                        {new Date(blog.created_at).toLocaleDateString(locale)}
                      </div>
                      <h3
                        data-ea-visual-tag="h6"
                        className={cn('line-clamp-2 text-start font-bold', 'ea-blog-card__title')}
                      >
                        {blog.name}
                      </h3>
                    </div>
                  </CardContent>
                </Link>
              </Card>
            );
          });
        })}
      </div>
      {blogs?.pages.length === 1 && blogs?.pages?.every((page) => page.length === 0) && (
        <div className="flex w-full items-center justify-center">
          <p className="text-foreground">{t('text554')}</p>
        </div>
      )}
      <div className="flex self-center">
        {blogs?.pages.length > 0 && blogs?.pages[blogs?.pages.length - 1].length > 0 && (
          <Button
            onClick={() => fetchNextPage()}
            disabled={isFetching}
            className="rounded-none bg-primary text-white"
          >
            {t('text338')}
          </Button>
        )}
      </div>
    </>
  );
};
